import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { ArrowRight, Archive } from "lucide-react";
import { API } from "../App";

/**
 * "From the Archive" — surfaces 2 random Tumblr Archive articles on each visit.
 * Distinct visual treatment (sepia/grain accent) signals legacy content.
 */
const ArchiveWidget = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/articles/archive/random?count=2`)
      .then((res) => {
        if (!cancelled) setArticles(res.data.articles || []);
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || articles.length === 0) return null;

  return (
    <section
      className="relative py-24 md:py-32 border-t border-border overflow-hidden"
      data-testid="archive-widget"
    >
      {/* Subtle vintage texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.5) 2px, rgba(255,255,255,0.5) 3px)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="flex items-end justify-between mb-12 border-b border-border pb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Archive className="h-4 w-4 text-primary" />
              <span
                className="font-mono text-xs uppercase tracking-[0.3em] text-primary"
                data-testid="archive-widget-label"
              >
                From the Archive
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
              Vintage Cuts
            </h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-md">
              Stories rescued from our locked Tumblr — handpicked at random.
            </p>
          </div>
          <Link to="/category/tumblr-archive" className="hidden md:block">
            <span
              className="font-mono text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors inline-flex items-center gap-2"
              data-testid="archive-widget-view-all"
            >
              Browse Archive
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        </div>

        {/* Two-column archival layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
          {articles.map((article, idx) => (
            <Link
              key={article.article_id}
              to={`/articles/${article.slug}`}
              className="group block"
              data-testid={`archive-widget-article-${idx}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden mb-5 border border-border">
                <img
                  src={
                    article.featured_image ||
                    "https://images.unsplash.com/photo-1692663576031-ff42f3b4652d?w=800"
                  }
                  alt={article.title}
                  className="w-full h-full object-cover img-grayscale group-hover:scale-[1.03] group-hover:grayscale-0 transition-all duration-700 ease-out"
                />
                {/* Archive ribbon */}
                <div className="absolute top-0 left-0 bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                  Archive
                </div>
                {/* Bottom date strip */}
                <div className="absolute bottom-0 left-0 right-0 bg-background/90 backdrop-blur-sm px-4 py-2 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {article.published_at
                      ? format(new Date(article.published_at), "MMM yyyy")
                      : format(new Date(article.created_at), "MMM yyyy")}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {Math.max(1, Math.ceil((article.content?.split(/\s+/).length || 0) / 200))} min read
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    №&nbsp;{String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
              </div>
              <h3 className="font-heading font-bold text-xl md:text-2xl uppercase tracking-tight leading-tight group-hover:text-primary transition-colors">
                {article.title}
              </h3>
              {article.excerpt && (
                <p className="mt-3 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                  {article.excerpt}
                </p>
              )}
            </Link>
          ))}
        </div>

        {/* Mobile view-all */}
        <div className="mt-12 md:hidden">
          <Link to="/category/tumblr-archive">
            <span
              className="font-mono text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors inline-flex items-center gap-2"
              data-testid="archive-widget-view-all-mobile"
            >
              Browse Archive
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ArchiveWidget;
